<script lang="ts">
  import { onMount } from "svelte";
  import { mountChart, darkTooltip } from "../../lib/echartsHelpers";
  import type { DerivedWorkout } from "../../lib/lapAnalysis";
  import { strokeColor, strokeAbbrev, COLORS } from "../../lib/designTokens";

  interface Props { dw: DerivedWorkout }
  let { dw }: Props = $props();

  // Default goal: 2km/swim. Will become configurable + history-aware later.
  const GOAL_METERS = 2000;

  let pct = $derived(Math.min(1, dw.totalDistanceMeters / GOAL_METERS));
  let closed = $derived(pct >= 1);
  let dominantStroke = $derived(dw.strokeBreakdown[0]?.style ?? "Mixed");
  let totalSeconds = $derived(dw.totalDurationSeconds);
  let mins = $derived(Math.floor(totalSeconds / 60));
  let secs = $derived(Math.floor(totalSeconds % 60));

  let el: HTMLDivElement;

  onMount(() => {
    // Each lap = one arc segment. Segment angular width = lap.durationSeconds
    // proportional. If goal is met, the ring spans 360°; otherwise it spans
    // 360° × pct and the remainder is rendered as a dim "uncompleted" arc.
    const lapData = dw.laps.map((lap) => {
      const isFly = lap.strokeStyle === "Butterfly";
      return {
        value: lap.durationSeconds,
        name: `L${lap.index} ${lap.strokeStyle}`,
        itemStyle: {
          color: strokeColor(lap.strokeStyle),
          // Fly gets a subtle bloom — "the hard work earns the light"
          shadowBlur: isFly ? 8 : 0,
          shadowColor: isFly ? strokeColor("Butterfly") : "transparent",
          borderColor: COLORS.navyMid,
          borderWidth: 1.2,
        },
      };
    });

    // If ring isn't closed, append a "remaining" placeholder slice
    const totalLapSeconds = dw.laps.reduce((s, l) => s + l.durationSeconds, 0);
    const remaining = closed
      ? null
      : {
          value: totalLapSeconds * (1 / pct - 1),
          name: "",
          itemStyle: {
            color: "rgba(255,255,255,0.04)",
            borderColor: "transparent",
            borderWidth: 0,
          },
          label: { show: false },
          emphasis: { disabled: true },
        };

    const data = remaining ? [...lapData, remaining] : lapData;

    return mountChart(el, (chart) => chart.setOption({
      backgroundColor: "transparent",
      tooltip: {
        trigger: "item",
        ...darkTooltip((p: { name: string; value: number; dataIndex: number }) => {
          if (!p.name) return "";
          const lap = dw.laps[p.dataIndex];
          if (!lap) return p.name;
          return `<strong>L${lap.index}</strong> · ${lap.strokeStyle}<br/>${Math.round(lap.durationSeconds)}s · ${lap.spl ?? "?"} strokes · SWOLF ${lap.swolf ?? "?"}`;
        }),
      },
      series: [
        {
          type: "pie",
          radius: ["68%", "92%"],
          startAngle: 90,
          avoidLabelOverlap: false,
          padAngle: 0.3,
          label: { show: false },
          labelLine: { show: false },
          data,
          // Lift each segment slightly on hover
          emphasis: {
            scale: true,
            scaleSize: 4,
          },
          animationType: "scale" as const,
          animationDuration: 900,
          animationEasing: "cubicOut" as const,
        },
      ],
    }));
  });
</script>

<article class="proto silk-ring">
  <header>
    <span class="num">07</span>
    <h2>Silk Ring ✦</h2>
    <p class="story">
      One ring. One swim. Each arc is a length, colored by stroke. Closes at {GOAL_METERS}m.
    </p>
  </header>

  <div class="stage">
    <div class="ring" bind:this={el}></div>
    <div class="center">
      <div class="pct">{Math.round(pct * 100)}%</div>
      {#if closed}
        <div class="badge">CLOSED</div>
      {:else}
        <div class="badge incomplete">{Math.round((GOAL_METERS - dw.totalDistanceMeters))}m to go</div>
      {/if}
      <div class="meta">
        <div class="meta-row"><span>{dw.laps.length}×</span><span class="m">lengths</span></div>
        <div class="meta-row"><span>{dw.totalDistanceMeters}<small>m</small></span></div>
        <div class="meta-row"><span>{mins}:{secs.toString().padStart(2, "0")}</span></div>
      </div>
      <div class="dominant" style:color={strokeColor(dominantStroke)}>
        mostly {dominantStroke}
      </div>
    </div>
  </div>

  <div class="legend">
    {#each dw.strokeBreakdown as s, i (i)}
      <div class="leg-pill">
        <span class="dot" style:background={strokeColor(s.style)}></span>
        <span class="leg-name">{strokeAbbrev(s.style)}</span>
        <span class="leg-count">{s.lengths}×</span>
      </div>
    {/each}
  </div>

  {#if dw.swolfStats}
    <div class="silk-footer">
      <span class="silk-icon">✦</span>
      <span class="silk-text">
        <strong>{dw.swolfStats.bestLaps.length}</strong> silk sets today ·
        lowest SWOLF <strong>{dw.swolfStats.bestLaps[0].swolf}</strong>
        ({dw.swolfStats.bestLaps[0].strokeStyle.toLowerCase()})
      </span>
    </div>
  {/if}
</article>

<style>
  .proto { background: var(--bg-card); border-radius: 16px; padding: 1.25rem; margin: 0.75rem 0; }
  .silk-ring { background: linear-gradient(160deg, var(--bg-card) 0%, var(--bg-canvas) 100%); }
  header { margin-bottom: 1rem; }
  .num { display: inline-block; padding: 0.1rem 0.5rem; background: var(--accent-fire); color: var(--bg-canvas); font-size: 0.7rem; font-weight: 700; border-radius: 3px; letter-spacing: 1px; }
  h2 { font-size: 1.25rem; font-weight: 800; margin: 0.4rem 0 0.2rem; letter-spacing: -0.01em; }
  .story { font-size: 0.78rem; color: var(--text-muted); margin: 0; }

  .stage { position: relative; width: 100%; aspect-ratio: 1; max-width: 340px; margin: 0 auto; }
  .ring { width: 100%; height: 100%; }
  .center {
    position: absolute; inset: 0;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    pointer-events: none;
    gap: 0.05rem;
  }
  .pct {
    font-size: 3.2rem; font-weight: 800; line-height: 1;
    color: var(--text-primary);
    letter-spacing: -0.04em;
    font-variant-numeric: tabular-nums;
  }
  .badge {
    font-size: 0.7rem; font-weight: 700; letter-spacing: 2px;
    color: var(--accent-primary);
    margin-top: 0.2rem;
    padding: 0.15rem 0.55rem;
    background: rgba(183,199,232,0.12);
    border: 1px solid rgba(183,199,232,0.3);
    border-radius: 3px;
  }
  .badge.incomplete {
    color: var(--text-muted);
    background: rgba(143,144,152,0.1);
    border-color: rgba(143,144,152,0.3);
  }
  .meta {
    display: flex; gap: 0.65rem;
    margin-top: 0.6rem;
    font-size: 0.78rem;
    font-variant-numeric: tabular-nums;
  }
  .meta-row { display: flex; align-items: baseline; gap: 0.15rem; }
  .meta-row span { font-weight: 700; color: var(--text-primary); }
  .meta-row small { font-size: 0.65rem; color: var(--text-muted); font-weight: 400; margin-left: 0.05rem; }
  .meta-row .m { font-size: 0.7rem; color: var(--text-muted); font-weight: 400; }

  .dominant {
    margin-top: 0.4rem;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
  }

  .legend {
    display: flex; flex-wrap: wrap; gap: 0.4rem;
    justify-content: center;
    margin-top: 0.75rem;
  }
  .leg-pill {
    display: inline-flex; align-items: center; gap: 0.35rem;
    padding: 0.25rem 0.55rem;
    background: var(--bg-canvas);
    border-radius: 12px;
    font-size: 0.72rem;
    font-variant-numeric: tabular-nums;
  }
  .dot { width: 8px; height: 8px; border-radius: 50%; }
  .leg-name { font-weight: 700; letter-spacing: 0.5px; font-size: 0.68rem; }
  .leg-count { color: var(--text-muted); }

  .silk-footer {
    margin-top: 0.9rem;
    padding: 0.6rem 0.75rem;
    background: rgba(183,199,232,0.08);
    border: 1px solid color-mix(in srgb, var(--accent-primary) 30%, transparent);
    border-radius: 4px;
    display: flex; align-items: center; gap: 0.5rem;
    font-size: 0.78rem;
  }
  .silk-icon { color: var(--accent-primary); font-size: 1rem; }
  .silk-text strong { color: var(--accent-primary); }
</style>
